import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Title, useNotify, usePermissions } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { fetchAndStoreTenantBrandingTheme } from "@/utils/tenant-branding-cache";
import {
  RAZA_PRINT_PAPER_SIZE_OPTIONS,
  toTenantSettingsFormState,
  toTenantSettingsPatch,
  type TenantSettingsFormState,
} from "./tenant-settings-form";
import { resolveRazaPrintPaperSize } from "@/utils/raza-print-paper-size";

const emptyState = (): TenantSettingsFormState => toTenantSettingsFormState({});

export default function TenantSettingsPage() {
  const notify = useNotify();
  const { permissions } = usePermissions();
  const canView = hasPermission(permissions, "tenantSettings.view");
  const canEdit = hasPermission(permissions, "tenantSettings.edit");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<TenantSettingsFormState>(emptyState);

  const load = useCallback(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    callApi({ location: "tenantSettings", method: "GET" })
      .then((response) => {
        setForm(toTenantSettingsFormState(response.data as TenantSettingsFormState));
      })
      .catch(() => notify("Failed to load app settings", { type: "error" }))
      .finally(() => setLoading(false));
  }, [canView, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const response = await callApi({
        location: "tenantSettings",
        method: "PATCH",
        data: toTenantSettingsPatch(form),
      });
      setForm(toTenantSettingsFormState(response.data as TenantSettingsFormState));
      await fetchAndStoreTenantBrandingTheme().catch(() => undefined);
      notify("App settings saved", { type: "success" });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      notify(err?.response?.data?.message || err?.message || "Failed to save settings", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <>
      <Title title="App settings" />
      <Box sx={{ p: 2, maxWidth: 560 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          App settings
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Product and hardcopy preferences (not cron or WhatsApp).
        </Alert>

        {!canEdit ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You can view these settings but do not have permission to edit them.
          </Alert>
        ) : null}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            select
            label="Raza print paper size"
            size="small"
            value={form.razaPrintPaperSize}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                razaPrintPaperSize: resolveRazaPrintPaperSize(e.target.value),
              }))
            }
            helperText="Applies to raza hardcopy print layout (A4 default, A5 compact, A5 rotated 90°)."
            disabled={!canEdit || saving}
          >
            {RAZA_PRINT_PAPER_SIZE_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Google Calendar ID"
            size="small"
            value={form.googleCalendarId}
            onChange={(e) => setForm((prev) => ({ ...prev, googleCalendarId: e.target.value }))}
            helperText="Calendar used for raza event sync. Leave blank to unlink."
            disabled={!canEdit || saving}
          />
          {canEdit ? (
            <Box>
              <Button onClick={handleSave} variant="contained" disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </Box>
          ) : null}
        </Box>
      </Box>
    </>
  );
}
